/**
 * Встроенные индикаторы (ТЗ, раздел 4.3).
 *
 * Каждое определение описывает себя целиком: параметры, выходы, шкалу,
 * размещение и подпись. График не знает, что такое RSI, — он умеет
 * рисовать линии и гистограммы по тому, что вернул `calc`. Добавление
 * восьмого индикатора не требует правок графика: это ровно тот же приём,
 * которым бот описывает свои настройки через paramsSchema.
 */

import { alignToTimeframe, type DerivativePoint, type Timeframe } from '@specter/types';
import type { Candle } from '../state/store.js';
import { atr, ema, emaOfSparse, rollingStdDev, rsi, sma } from './math.js';
import { volumeProfile } from './profile.js';
import {
  RANK_ZONES,
  ZONE_FADE,
  ZONE_READOUT_TIMEFRAMES,
  ZONE_TIMEFRAMES,
  ZONE_WARMUP,
  timeframeRankFrames,
  timeframeRanks,
  zoneAlpha,
  zoneReading,
  zoneSeries,
} from './zones.js';
import type { SignalOptions } from './signals.js';
import {
  alignedOf,
  cumulativeOf,
  divergenceNow,
  divergenceScan,
  zonesProAnalysis,
  zonesProState,
  type Bar,
  type Divergence,
  type DivergenceSettings,
  type DivergenceSide,
  type DivergenceSource,
  type DivergenceWatchOptions,
  type ZonesProShape,
} from '@specter/rules';
import {
  alphaOf,
  earliest,
  halfHeightOf,
  lastAtr,
  liquidityMap,
  resample,
  stepOf,
} from './liquidity.js';
import type { IndicatorShape, ReadoutLegendItem, ReadoutRow } from './overlays.js';
import { TABLE_PARAM, valuesOf } from './explain.js';
import { explainFlow } from './explain-flow.js';
import { IMBALANCES } from './imbalances.js';
import { ORDER_BLOCKS } from './orderblocks.js';
import { FIBO_CHECK } from './fibo-check.js';
import { ZONES_PRO2 } from './zones-pro2.js';
import { SESSIONS_INDICATOR } from './sessions.js';
import { explainLiquidity, explainProfile } from './explain-levels.js';
import {
  explainAtr,
  explainAverage,
  explainBollinger,
  explainMacd,
  explainRsi,
  explainTriple,
  explainVolume,
} from './explain-classic.js';
import { ORDER_FLOW } from './order-flow.js';
import { ORDER_FLOW_CLUSTERS, ORDER_FLOW_IMBALANCE, ORDER_FLOW_LARGE } from './order-flow-bars.js';
import { ORDER_BOOK_HEATMAP, ORDER_FLOW_DELTA, ORDER_FLOW_LIQUIDATIONS } from './order-flow-stage3.js';
import { CROWD_POSITIONING, MARKET_LIQUIDATIONS, MI_SCORE } from './positioning.js';
import type { LiquidityLevel } from './liquidity.js';
import type { ZoneSeries } from './zones.js';
import {
  boolParam,
  colorParam,
  numberParam,
  priceOf,
  selectParam,
  sourceParam,
  type IndicatorDefinition,
  type IndicatorContext,
  type IndicatorParams,
  type IndicatorParamSpec,
  type IndicatorPoint,
} from './types.js';

const COLORS = {
  brand: '#6E8BFF',
  amber: '#E0A458',
  cyan: '#5AC8D8',
  violet: '#9B8CE0',
  green: '#3FBF87',
  red: '#F0616F',
  grey: '#79818F',
} as const;

/** Склеивает времена баров со значениями индикатора. */
function points(
  candles: readonly Candle[],
  values: readonly (number | null)[],
): IndicatorPoint[] {
  return candles.map((candle, i) => ({ time: candle.time, value: values[i] ?? null }));
}

const sourceSpec = {
  key: 'source',
  label: 'Источник',
  hint:
    'Какая цена свечи идёт в расчёт. Обычно закрытие: оно устойчивее прочих — ' +
    'максимум и минимум может поставить одна случайная сделка.',
  type: 'source',
  default: 'close',
} as const;

const periodSpec = (
  key: string,
  label: string,
  value: number,
  /*
   * Объяснение здесь — обязательный довод, а не последний необязательный.
   *
   * «Период» ничего не говорит тому, кто видит индикатор впервые, и у
   * каждого значит своё: у RSI это окно силы движения, у ATR — окно
   * размаха. Общей фразы на всех тут быть не может, поэтому и не
   * получится её забыть.
   */
  hint: string,
  max = 500,
): {
  key: string;
  label: string;
  hint: string;
  type: 'number';
  default: number;
  min: number;
  max: number;
  step: number;
} => ({ key, label, hint, type: 'number', default: value, min: 1, max, step: 1 });

// --- скользящие средние ---------------------------------------------------

const SMA: IndicatorDefinition = {
  id: 'sma',
  name: 'SMA',
  description: 'Простое скользящее среднее',
  placement: 'overlay',
  params: [
    periodSpec(
      'period',
      'Период',
      20,
      'Сколько последних свечей усредняется. Больше — линия ровнее и поворачивает позже, меньше — жмётся к цене и чаще дёргается.',
    ),
    sourceSpec,
    TABLE_PARAM,
  ],
  outputs: [{ key: 'sma', label: 'SMA', type: 'line', defaultColor: COLORS.brand }],
  readout: (candles, p, context) =>
    explainAverage(candles, valuesOf(SMA.calc(candles, p, context).sma), context.livePartial).rows,
  warmup: (p) => numberParam(p, 'period', 20),
  label: (p) => `SMA (${numberParam(p, 'period', 20)})`,
  calc: (candles, p) => {
    const source = sourceParam(p, 'source');
    const values = candles.map((c) => priceOf(c, source));
    return { sma: points(candles, sma(values, numberParam(p, 'period', 20))) };
  },
};

const EMA: IndicatorDefinition = {
  id: 'ema',
  name: 'EMA',
  description: 'Экспоненциальное скользящее среднее',
  placement: 'overlay',
  params: [
    periodSpec(
      'period',
      'Период',
      21,
      'Сколько свечей в расчёте. Свежие весят больше, чем у простой средней, — поэтому при том же периоде EMA поворачивает раньше.',
    ),
    sourceSpec,
    TABLE_PARAM,
  ],
  outputs: [{ key: 'ema', label: 'EMA', type: 'line', defaultColor: COLORS.cyan }],
  readout: (candles, p, context) =>
    explainAverage(candles, valuesOf(EMA.calc(candles, p, context).ema), context.livePartial).rows,
  warmup: (p) => numberParam(p, 'period', 21),
  label: (p) => `EMA (${numberParam(p, 'period', 21)})`,
  calc: (candles, p) => {
    const source = sourceParam(p, 'source');
    const values = candles.map((c) => priceOf(c, source));
    return { ema: points(candles, ema(values, numberParam(p, 'period', 21))) };
  },
};

/**
 * Три скользящих средних одним индикатором.
 *
 * Стандартный набор биржевых терминалов: короткая, средняя и длинная
 * средние на одном графике. Можно было бы добавить три отдельных SMA и
 * получить то же самое, но тогда каждая заняла бы свою строку в легенде
 * и настраивалась бы отдельно — а смотрят на них всегда вместе, потому
 * что смысл именно в их взаимном расположении.
 *
 * Периоды 5, 10 и 30 — набор по умолчанию у большинства площадок.
 * Тип средней выбирается один на все три: сравнивать простую с
 * экспоненциальной бессмысленно, они разной природы.
 */
const TRIPLE_MA: IndicatorDefinition = {
  id: 'ma3',
  name: '3 MA',
  description: 'Три скользящих средних: короткая, средняя и длинная',
  placement: 'overlay',
  params: [
    periodSpec(
      'fast',
      'Короткая',
      5,
      'Самая быстрая из трёх. Разворачивается первой, и её пересечение с остальными обычно и считают сигналом.',
    ),
    periodSpec(
      'mid',
      'Средняя',
      10,
      'Опора движения. Цена выше неё — тренд обычно ещё держится, ниже — под вопросом.',
    ),
    periodSpec(
      'slow',
      'Длинная',
      30,
      'Самая медленная. Показывает направление в целом и не замечает дневной суеты.',
    ),
    {
      key: 'kind',
      label: 'Тип средней',
      hint:
        'Как считается среднее. Простое — все свечи равны; экспоненциальное — свежие весят больше, и линия поворачивает раньше.',
      type: 'select',
      default: 'sma',
      options: [
        { value: 'sma', label: 'Простая (SMA)' },
        { value: 'ema', label: 'Экспоненциальная (EMA)' },
      ],
    },
    sourceSpec,
    {
      key: 'marks',
      label: 'Отметки на цене',
      hint: 'Треугольники там, где короткая средняя пересекла длинную. Выключите, если они мешают смотреть на свечи: табло и линии останутся.',
      type: 'boolean',
      default: true,
    },
    TABLE_PARAM,
  ],
  readout: (candles, p, context) => tripleExplained(candles, p, context).rows,
  shapes: (candles, p, context) => (boolParam(p, 'marks', true) ? tripleExplained(candles, p, context).marks : []),
  legend: [
    {
      sample: 'strong',
      term: 'Треугольник на цене — ',
      text: 'короткая средняя пересекла длинную: вверх — рост может начаться, вниз — снижение. Во флэте такие пересечения часто ложные.',
    },
  ],
  outputs: [
    { key: 'fast', label: 'Короткая', type: 'line', defaultColor: COLORS.amber },
    { key: 'mid', label: 'Средняя', type: 'line', defaultColor: COLORS.cyan },
    { key: 'slow', label: 'Длинная', type: 'line', defaultColor: COLORS.violet },
  ],
  // Прогрев по самой длинной: пока её нет, набор неполон.
  warmup: (p) => numberParam(p, 'slow', 30),
  label: (p) =>
    `${numberParam(p, 'fast', 5)}/${numberParam(p, 'mid', 10)}/${numberParam(p, 'slow', 30)} ` +
    `${selectParam(p, 'kind', 'sma') === 'ema' ? 'EMA' : 'MA'}`,
  calc: (candles, p) => {
    const source = sourceParam(p, 'source');
    const values = candles.map((c) => priceOf(c, source));
    const average = selectParam(p, 'kind', 'sma') === 'ema' ? ema : sma;

    return {
      fast: points(candles, average(values, numberParam(p, 'fast', 5))),
      mid: points(candles, average(values, numberParam(p, 'mid', 10))),
      slow: points(candles, average(values, numberParam(p, 'slow', 30))),
    };
  },
};

/** Выводы трёх средних: и табло, и отметки считаются из одних рядов. */
function tripleExplained(candles: readonly Candle[], p: IndicatorParams, context: IndicatorContext) {
  const lines = TRIPLE_MA.calc(candles, p, context);
  return explainTriple(
    candles,
    valuesOf(lines.fast),
    valuesOf(lines.mid),
    valuesOf(lines.slow),
    context.livePartial,
  );
}

function macdExplained(candles: readonly Candle[], p: IndicatorParams, context: IndicatorContext) {
  const lines = MACD.calc(candles, p, context);
  return explainMacd(
    candles,
    valuesOf(lines.macd),
    valuesOf(lines.signal),
    valuesOf(lines.histogram),
    context.livePartial,
  );
}

// --- полосы Боллинджера ---------------------------------------------------

const BOLLINGER: IndicatorDefinition = {
  id: 'bb',
  name: 'Bollinger Bands',
  description: 'Полосы Боллинджера: SMA плюс-минус отклонения',
  placement: 'overlay',
  params: [
    periodSpec(
      'period',
      'Период',
      20,
      'За сколько свечей считаются средняя и размах вокруг неё. Больше — полосы шире и спокойнее, меньше — теснее и чаще касаются цены.',
    ),
    { key: 'mult', label: 'Отклонений', hint: 'На сколько размахов полосы отступают от средней. Два — привычно: внутрь попадает почти всё движение, и выход за полосу уже событие.', type: 'number', default: 2, min: 0.1, max: 10, step: 0.1 },
    sourceSpec,
    TABLE_PARAM,
  ],
  readout: (candles, p, context) => {
    const bands = BOLLINGER.calc(candles, p, context);
    return explainBollinger(
      candles,
      valuesOf(bands.upper),
      valuesOf(bands.middle),
      valuesOf(bands.lower),
      context.livePartial,
    ).rows;
  },
  outputs: [
    { key: 'upper', label: 'Верхняя', type: 'line', defaultColor: COLORS.violet },
    { key: 'middle', label: 'Средняя', type: 'line', defaultColor: COLORS.grey },
    { key: 'lower', label: 'Нижняя', type: 'line', defaultColor: COLORS.violet },
  ],
  warmup: (p) => numberParam(p, 'period', 20),
  label: (p) => `BB (${numberParam(p, 'period', 20)}, ${numberParam(p, 'mult', 2)})`,
  calc: (candles, p) => {
    const period = numberParam(p, 'period', 20);
    const mult = numberParam(p, 'mult', 2);
    const source = sourceParam(p, 'source');
    const values = candles.map((c) => priceOf(c, source));

    const middle = sma(values, period);
    const deviation = rollingStdDev(values, period);

    const upper = middle.map((m, i) => {
      const d = deviation[i];
      return m === null || d === null || d === undefined ? null : m + mult * d;
    });
    const lower = middle.map((m, i) => {
      const d = deviation[i];
      return m === null || d === null || d === undefined ? null : m - mult * d;
    });

    return {
      upper: points(candles, upper),
      middle: points(candles, middle),
      lower: points(candles, lower),
    };
  },
};

// --- осцилляторы ----------------------------------------------------------

const RSI: IndicatorDefinition = {
  id: 'rsi',
  name: 'RSI',
  description: 'Индекс относительной силы',
  placement: 'pane',
  params: [
    periodSpec(
      'period',
      'Период',
      14,
      'За сколько свечей меряется сила движения. Четырнадцать — привычное значение; меньше — RSI чаще забирается в крайние зоны.',
      200,
    ),
    {
      key: 'marks',
      label: 'Отметки на цене',
      hint: 'Треугольник там, где RSI вышел из перекупленности или перепроданности — первый признак, что резкое движение выдохлось.',
      type: 'boolean',
      default: true,
    },
    TABLE_PARAM,
  ],
  outputs: [{ key: 'rsi', label: 'RSI', type: 'line', defaultColor: COLORS.brand }],
  readout: (candles, p, context) =>
    explainRsi(candles, valuesOf(RSI.calc(candles, p, context).rsi), context.livePartial).rows,
  shapes: (candles, p, context) =>
    boolParam(p, 'marks', true)
      ? explainRsi(candles, valuesOf(RSI.calc(candles, p, context).rsi), context.livePartial).marks
      : [],
  legend: [
    {
      sample: 'strong',
      term: 'Треугольник на цене — ',
      text: 'RSI вернулся из крайней зоны: вниз из перекупленности (выше 70), вверх из перепроданности (ниже 30). Резкое движение выдохлось.',
    },
  ],
  scale: {
    min: 0,
    max: 100,
    levels: [
      { value: 70, color: COLORS.red, dashed: true },
      { value: 50, color: COLORS.grey, dashed: true },
      { value: 30, color: COLORS.green, dashed: true },
    ],
  },
  warmup: (p) => numberParam(p, 'period', 14) + 1,
  label: (p) => `RSI (${numberParam(p, 'period', 14)})`,
  calc: (candles, p) => {
    const closes = candles.map((c) => c.close);
    return { rsi: points(candles, rsi(closes, numberParam(p, 'period', 14))) };
  },
};

const MACD: IndicatorDefinition = {
  id: 'macd',
  name: 'MACD',
  description: 'Схождение и расхождение скользящих средних',
  placement: 'pane',
  params: [
    periodSpec(
      'fast',
      'Быстрая',
      12,
      'Короткая средняя. Чем короче, тем быстрее MACD откликается на разворот — и тем чаще принимает за него обычный откат.',
      200,
    ),
    periodSpec(
      'slow',
      'Медленная',
      26,
      'Длинная средняя. Задаёт, какое движение считать основным: сам MACD — это разница между ней и короткой.',
      200,
    ),
    periodSpec(
      'signal',
      'Сигнальная',
      9,
      'Сглаживание самой линии MACD. Её пересечение с этой линией и считают сигналом.',
      200,
    ),
    {
      key: 'marks',
      label: 'Отметки на цене',
      hint: 'Треугольник на сильных пересечениях: вверх — когда MACD ниже нуля, вниз — когда выше. Пересечения по тренду случаются на каждом откате и не отмечаются.',
      type: 'boolean',
      default: true,
    },
    TABLE_PARAM,
  ],
  readout: (candles, p, context) => macdExplained(candles, p, context).rows,
  shapes: (candles, p, context) => (boolParam(p, 'marks', true) ? macdExplained(candles, p, context).marks : []),
  legend: [
    {
      sample: 'strong',
      term: 'Треугольник на цене — ',
      text: 'MACD пересёк сигнальную против общего тренда: вверх под нулём, вниз над нулём. Такой разворот стоит внимания.',
    },
  ],
  outputs: [
    { key: 'macd', label: 'MACD', type: 'line', defaultColor: COLORS.cyan },
    { key: 'signal', label: 'Сигнальная', type: 'line', defaultColor: COLORS.brand },
    { key: 'histogram', label: 'Гистограмма', type: 'histogram', defaultColor: COLORS.grey },
  ],
  scale: { levels: [{ value: 0, color: COLORS.grey }] },
  warmup: (p) => numberParam(p, 'slow', 26) + numberParam(p, 'signal', 9),
  label: (p) =>
    `MACD (${numberParam(p, 'fast', 12)}, ${numberParam(p, 'slow', 26)}, ${numberParam(p, 'signal', 9)})`,
  calc: (candles, p) => {
    const closes = candles.map((c) => c.close);
    const fast = ema(closes, numberParam(p, 'fast', 12));
    const slow = ema(closes, numberParam(p, 'slow', 26));

    const macdLine = fast.map((f, i) => {
      const s = slow[i];
      return f === null || s === null || s === undefined ? null : f - s;
    });
    // Сигнальная считается по самой линии MACD, а та начинается позже
    // нулевого бара — поэтому EMA по разреженному ряду.
    const signalLine = emaOfSparse(macdLine, numberParam(p, 'signal', 9));
    const histogram = macdLine.map((m, i) => {
      const s = signalLine[i];
      return m === null || s === null || s === undefined ? null : m - s;
    });

    return {
      macd: points(candles, macdLine),
      signal: points(candles, signalLine),
      histogram: points(candles, histogram),
    };
  },
};

const ATR: IndicatorDefinition = {
  id: 'atr',
  name: 'ATR',
  description: 'Средний истинный диапазон — мера волатильности',
  placement: 'pane',
  params: [
    periodSpec(
      'period',
      'Период',
      14,
      'За сколько свечей усредняется размах. ATR не говорит о направлении — только о том, насколько широко ходит цена; по нему и ставят стоп.',
      200,
    ),
    TABLE_PARAM,
  ],
  readout: (candles, p, context) =>
    explainAtr(candles, valuesOf(ATR.calc(candles, p, context).atr), context.livePartial).rows,
  outputs: [{ key: 'atr', label: 'ATR', type: 'line', defaultColor: COLORS.violet }],
  warmup: (p) => numberParam(p, 'period', 14),
  label: (p) => `ATR (${numberParam(p, 'period', 14)})`,
  calc: (candles, p) => {
    const period = numberParam(p, 'period', 14);
    const values = atr(
      candles.map((c) => c.high),
      candles.map((c) => c.low),
      candles.map((c) => c.close),
      period,
    );
    return { atr: points(candles, values) };
  },
};

// --- объём ----------------------------------------------------------------

/**
 * Объём.
 *
 * Такой же индикатор, как остальные, — его добавляют и убирают. Раньше
 * панель объёма была вшита в график и убрать её было нельзя; на
 * инструментах, где объём ничего не говорит, она просто отъедала высоту.
 *
 * Столбцы красятся по направлению свечи, а не по знаку значения: объём
 * всегда положителен, и знак здесь ничего не различает.
 */
const VOLUME: IndicatorDefinition = {
  id: 'volume',
  name: 'Объём',
  description: 'Объём торгов по барам, отдельной панелью',
  placement: 'volume',
  params: [TABLE_PARAM],
  readout: (candles, _p, context) => explainVolume(candles, context.livePartial).rows,
  outputs: [
    {
      key: 'volume',
      label: 'Объём',
      type: 'histogram',
      defaultColor: COLORS.grey,
      format: 'volume',
    },
  ],
  warmup: () => 0,
  label: () => 'Объём',
  calc: (candles) => ({
    volume: candles.map((candle) => ({
      time: candle.time,
      value: candle.volume,
      color: candle.close >= candle.open ? VOLUME_UP : VOLUME_DOWN,
    })),
  }),
};

/** Приглушённые тона: объём — фон для цены, а не соперник ей. */
/** Цвета столбиков объёма. График подменяет их цветами выбранного вида (см. chart/indicator-layer.ts). */
export const VOLUME_UP = 'rgba(63, 191, 135, 0.5)';
export const VOLUME_DOWN = 'rgba(240, 97, 111, 0.5)';

const VOLUME_MA: IndicatorDefinition = {
  id: 'volume-ma',
  name: 'Volume MA',
  description: 'Скользящее среднее объёма',
  // Ложится на уже существующую панель объёма, а не плодит новую.
  placement: 'volume',
  params: [
    periodSpec(
      'period',
      'Период',
      20,
      'Сколько свечей усредняется. Линия показывает обычный для инструмента объём — всплеск над ней виден сразу.',
      500,
    ),
  ],
  outputs: [{ key: 'ma', label: 'Volume MA', type: 'line', defaultColor: COLORS.brand }],
  warmup: (p) => numberParam(p, 'period', 20),
  label: (p) => `Volume MA (${numberParam(p, 'period', 20)})`,
  calc: (candles, p) => {
    const volumes = candles.map((c) => c.volume);
    return { ma: points(candles, sma(volumes, numberParam(p, 'period', 20))) };
  },
};

/**
 * Прозрачность полос карты и профиля, заданная человеком.
 *
 * Как у зон, ручка двигает всю картину разом и сохраняет разницу между
 * полосами: сильный уровень остаётся плотнее слабого, пиковая корзина —
 * плотнее пустой. Пятьдесят процентов — тот вид, что был до ручки; ноль —
 * вдвое плотнее, сто — полос не видно. Сплошной заливки не бывает и на
 * нуле: непрозрачная полоса закрыла бы свечи.
 */
export const FILL_TRANSPARENCY = 50;

export function fillAlpha(alpha: number, transparency: number): number {
  const t = Math.min(100, Math.max(0, transparency));
  return Math.min(0.95, (alpha * (100 - t)) / (100 - FILL_TRANSPARENCY));
}

const PROFILE_OPACITY: IndicatorParamSpec = {
  /*
   * Ключ не `opacity`: так называется прозрачность зон в «Выгодных зонах
   * PRO», а настройки индикатора живут в одном словаре — одинаковое имя
   * означало бы одно значение на двоих.
   */
  key: 'profileOpacity',
  label: 'Прозрачность профиля объёма, %',
  hint:
    'Насколько густо залиты полосы. Больше процент — бледнее, и свечи под ними видно лучше.',
  type: 'number',
  default: FILL_TRANSPARENCY,
  min: 0,
  max: 100,
  step: 5,
};

const LIQUIDITY_OPACITY: IndicatorParamSpec = {
  key: 'liquidityOpacity',
  label: 'Прозрачность ликвидности, %',
  hint:
    'Насколько густо залита карта. Больше процент — бледнее, и свечи под ней видно лучше.',
  type: 'number',
  default: FILL_TRANSPARENCY,
  min: 0,
  max: 100,
  step: 5,
};

/**
 * Профиль объёма.
 *
 * Первый авторский в списке — и не потому, что сложный, а потому что
 * отвечает на вопрос, которого классика не задаёт. Скользящая средняя
 * говорит, куда цена шла; профиль — где она задержалась. Уровень, вокруг
 * которого наторговали больше всего, притягивает цену обратно, а
 * провал в профиле она проходит быстро: там некому было её удержать.
 *
 * Значения по умолчанию — те же, что в пайне «Выгодные зоны PRO»:
 * человек, пришедший оттуда, должен увидеть ту же картинку.
 */
const VOLUME_PROFILE: IndicatorDefinition = {
  id: 'volume-profile',
  name: 'Профиль объёма',
  description:
    'Где наторговали больше всего. Диапазон окна делится на ценовые корзины, ' +
    'в каждой копится объём, полоса растёт от правого края влево.',
  author: 'specter',
  placement: 'overlay',
  params: [
    {
      key: 'window',
      label: 'Окно профиля, свечей',
      hint:
        'За какой отрезок собирается профиль. Больше — видно уровни, набранные давно; меньше — только то, что торгуют сейчас.',
      type: 'number',
      default: 300,
      min: 50,
      max: 1500,
      step: 10,
    },
    {
      key: 'bins',
      label: 'Количество корзин',
      hint:
        'На сколько ценовых полос делится размах. Больше корзин — тоньше полосы и точнее уровень, но картинка дробится.',
      type: 'number',
      default: 48,
      min: 10,
      max: 100,
      step: 1,
    },
    {
      key: 'length',
      label: 'Длина полос, свечей',
      hint:
        'Насколько далеко влево тянутся полосы. Это про вид, а не про расчёт: сам профиль от этого не меняется.',
      type: 'number',
      default: 140,
      min: 20,
      max: 500,
      step: 10,
    },
    {
      key: 'threshold',
      label: 'Порог отображения, % от максимума',
      hint:
        'Слабые полосы не рисуются вовсе. Ноль покажет все, включая пустые уровни; выше — останутся только заметные скопления.',
      type: 'number',
      default: 12,
      min: 1,
      max: 90,
      step: 1,
    },
    PROFILE_OPACITY,
    TABLE_PARAM,
  ],
  readout: (candles, p) =>
    explainProfile(
      candles,
      // Все корзины, а не только видимые: порог — про вид, а зона
      // стоимости считается по всему объёму.
      volumeProfile(candles, { window: numberParam(p, 'window', 300), bins: numberParam(p, 'bins', 48), threshold: 0 }),
    ),
  /*
   * Своих серий у профиля нет.
   *
   * Он рисуется полосами поперёк цены, а не линией во времени, — и
   * выход, который нечем заполнить, добавил бы в легенду строку с
   * вечным прочерком.
   */
  outputs: [],
  warmup: () => 0,
  calc: () => ({}),
  shapes: (candles, p) => {
    const last = candles[candles.length - 1];
    const step = stepOf(candles);
    if (!last || step <= 0) return [];

    const maxLength = numberParam(p, 'length', 140);
    const bands = volumeProfile(candles, {
      window: numberParam(p, 'window', 300),
      bins: numberParam(p, 'bins', 48),
      threshold: numberParam(p, 'threshold', 12),
    });

    return bands.map((band) => {
      // Длина задана в свечах, а не в цене: настройку человек выставляет
      // один раз, а масштаб меняет постоянно.
      const bars = Math.max(2, Math.round(maxLength * band.share));
      const mid = (band.low + band.high) / 2;

      return {
        kind: 'box' as const,
        from: last.time - bars * step,
        to: last.time,
        low: band.low,
        high: band.high,
        // Пустая корзина едва видна, пиковая заметна, но всё ещё
        // сквозная: непрозрачная полоса закрыла бы свечи.
        alpha: fillAlpha(
          (10 + band.share * 35) / 100,
          numberParam(p, 'profileOpacity', FILL_TRANSPARENCY),
        ),
        // Выше цены — сопротивление, ниже — поддержка.
        tone: (mid >= last.close ? 'down' : 'up') as 'up' | 'down',
      };
    });
  },
  label: (p) => `Профиль объёма (${numberParam(p, 'window', 300)})`,
};

/**
 * Карта ликвидности.
 *
 * Показывает не куда цена шла и не где задержалась, а за чем ей есть
 * смысл сходить: уровни, на которых остались чужие заявки и стопы.
 * Живой уровень тянется до правого края и насыщен; снятый обрывается
 * там, где его забрали, и гаснет.
 *
 * Наружу вынесены четыре настройки, и все четыре — про вид: сколько
 * уровней держать, когда забывать недостигнутые и какими цветами
 * красить стороны. Пороги, по которым уровень вообще появляется, —
 * состав индикатора, а не настройка, и живут в `liquidity.ts`.
 */
/*
 * Карта считается один раз на отрисовку: и полосы, и табло берут её
 * подряд с тем же рядом, а расчёт по тысяче свечей не бесплатный.
 */
let liquidityMemo: { candles: readonly Candle[]; key: string; levels: readonly LiquidityLevel[] } | null = null;

function liquidityOf(candles: readonly Candle[], levels: number, age: number): readonly LiquidityLevel[] {
  const key = `${levels}|${age}`;
  if (liquidityMemo && liquidityMemo.candles === candles && liquidityMemo.key === key) return liquidityMemo.levels;
  const result = liquidityMap(candles, { levels, age });
  liquidityMemo = { candles, key, levels: result };
  return result;
}

const LIQUIDITY_MAP: IndicatorDefinition = {
  id: 'liquidity-map',
  name: 'Карта ликвидности',
  description:
    'Где остались чужие заявки и стопы: объёмные свечи и экстремумы, ' +
    'включая часовой и четырёхчасовой. Снятый уровень обрывается и гаснет.',
  author: 'specter',
  placement: 'overlay',
  params: [
    {
      key: 'levels',
      label: 'Максимум уровней на карте',
      hint:
        'Сколько уровней держать на графике разом. Больше — полнее картина, но карта превращается в частокол.',
      type: 'number',
      default: 400,
      min: 5,
      max: 400,
      step: 5,
    },
    {
      key: 'age',
      label: 'Максимальный возраст уровня, свечей',
      hint:
        'Сколько свечей уровень помнится. Старый цена обычно уже забрала, и держать его — засорять карту.',
      type: 'number',
      default: 2500,
      min: 50,
      max: 5000,
      step: 50,
    },
    {
      key: 'below',
      label: 'Цвет ликвидности снизу',
      hint:
        'Каким цветом рисовать скопления под ценой — те стопы, за которыми цена может сходить вниз.',
      type: 'color',
      default: '#00C878',
    },
    {
      key: 'above',
      label: 'Цвет ликвидности сверху',
      hint:
        'Каким цветом рисовать скопления над ценой — то, за чем цена может сходить вверх.',
      type: 'color',
      default: '#FF3C50',
    },
    LIQUIDITY_OPACITY,
    TABLE_PARAM,
  ],
  outputs: [],
  /*
   * Карта — ровно та же, что раздел ликвидности в «Выгодных зонах PRO».
   *
   * Раньше она считалась по загруженному на графике — пятистам свечам,
   * — а зоны по глубокому ряду с Core; и рисовала всю историю, вместе с
   * вытесненными уровнями, которые зоны не показывают. Выходили две
   * разные карты при одних настройках, и отдельная менялась от прокрутки.
   * Теперь тот же ряд (`deepen`) и та же отрисовка (`liquidityShapes`).
   */
  deepOn: ZONE_READOUT_TIMEFRAMES.map((frame) => frame.timeframe),
  warmup: () => 0,
  calc: () => ({}),
  readout: (chartCandles, p, context) => {
    const candles = deepen(chartCandles, context);
    return explainLiquidity(candles, liquidityOf(candles, numberParam(p, 'levels', 400), numberParam(p, 'age', 2500)));
  },
  shapes: (chartCandles, p, context) => liquidityShapes(deepen(chartCandles, context), p),
  label: (p) => `Карта ликвидности (${numberParam(p, 'levels', 400)})`,
};

/**
 * Разобранный ряд: зоны, уровни карты и счёт сигнала.
 *
 * Всё это считается по одним и тем же свечам и нужно сразу троим —
 * `calc` рисует среднюю линию, `shapes` полосы и отметки, `readout`
 * пишет табло. Реестр зовёт их подряд с одним и тем же рядом, поэтому
 * достаточно помнить последний разбор: без этого зоны на пяти тысячах
 * баров считались бы четырежды за пересчёт, а он идёт на каждый тик.
 */
interface Analysis {
  readonly candles: readonly Candle[];
  readonly zones: ZoneSeries;
  readonly levels: readonly LiquidityLevel[];
}

let lastDeepened: {
  chart: readonly Candle[];
  loaded: readonly Candle[] | null;
  result: readonly Candle[];
} | null = null;

let lastAnalysis: (Analysis & { key: string }) | null = null;

/**
 * Ряд достаточной глубины для расчёта.
 *
 * График держит пятьсот свечей и догружает историю только по прокрутке —
 * а зоне нужно шестьсот на одно окно выборки. Пока их нет, индикатор
 * честно молчит: ни зон, ни сигналов. Выглядит это как «сигналы
 * пропали», хотя пропала история.
 *
 * Поэтому берётся тот же ряд, что индикатор уже просит у Core под свой
 * таймфрейм, а хвост дописывается с графика: там живой бар, которого у
 * Core ещё нет.
 */
function deepen(candles: readonly Candle[], context: IndicatorContext): readonly Candle[] {
  const loaded = context.series(context.timeframe);
  if (!loaded || loaded.length <= candles.length) return candles;

  /*
   * Склейка запоминается по ссылкам на исходные ряды: иначе каждый вызов
   * отдавал бы новый массив, и всё, что помнит разбор «по тому же ряду»,
   * промахивалось бы мимо памяти на каждом обращении.
   */
  if (lastDeepened && lastDeepened.chart === candles && lastDeepened.loaded === loaded) {
    return lastDeepened.result;
  }

  const edge = loaded[loaded.length - 1]!.time;
  const tail = candles.filter((candle) => candle.time > edge);
  const result = tail.length > 0 ? [...loaded, ...tail] : loaded;

  lastDeepened = { chart: candles, loaded, result };
  return result;
}

/** Зоны и карта по ряду; повторный вызов с тем же рядом бесплатен. */
function analyse(candles: readonly Candle[], p: IndicatorParams): Analysis {
  return zonesProAnalysis(candles, shapeOf(p));
}

/** Настройки карты ликвидности из параметров индикатора. */
function shapeOf(p: IndicatorParams): ZonesProShape {
  return { levels: numberParam(p, 'levels', 400), age: numberParam(p, 'age', 2500) };
}

/** Полосы профиля объёма как фигуры графика. */
function profileShapes(candles: readonly Candle[], p: IndicatorParams): IndicatorShape[] {
  const last = candles[candles.length - 1];
  const step = stepOf(candles);
  if (!last || step <= 0) return [];

  const maxLength = numberParam(p, 'length', 140);

  return volumeProfile(candles, {
    window: numberParam(p, 'window', 300),
    bins: numberParam(p, 'bins', 48),
    threshold: numberParam(p, 'threshold', 12),
  }).map((band) => {
    // Длина задана в свечах, а не в цене: настройку человек выставляет
    // один раз, а масштаб меняет постоянно.
    const bars = Math.max(2, Math.round(maxLength * band.share));
    const mid = (band.low + band.high) / 2;

    return {
      kind: 'box' as const,
      from: last.time - bars * step,
      to: last.time,
      low: band.low,
      high: band.high,
      // Пустая корзина едва видна, пиковая заметна, но всё ещё
      // сквозная: непрозрачная полоса закрыла бы свечи.
      alpha: fillAlpha((10 + band.share * 35) / 100, numberParam(p, 'profileOpacity', FILL_TRANSPARENCY)),
      // Выше цены — сопротивление, ниже — поддержка.
      tone: (mid >= last.close ? 'down' : 'up') as 'up' | 'down',
    };
  });
}

/** Полосы карты ликвидности как фигуры графика. */
function liquidityShapes(candles: readonly Candle[], p: IndicatorParams): IndicatorShape[] {
  const step = stepOf(candles);
  if (candles.length === 0 || step <= 0) return [];

  const atrValue = lastAtr(candles);
  const floor = earliest(candles, step);
  const below = colorParam(p, 'below', '#00C878');
  const above = colorParam(p, 'above', '#FF3C50');

  return liquidityMap(candles, {
    levels: numberParam(p, 'levels', 400),
    age: numberParam(p, 'age', 2500),
  })
    // На графике — действующая карта, а не вся её история: вытесненный
    // уровень с неё ушёл, и рисовать его значило бы показывать карту,
    // которой сейчас нет.
    .filter((level) => level.evictedAt === null)
    .map((level) => {
      const half = halfHeightOf(atrValue, level.power);
      const alive = level.to === null;

      return {
        kind: 'box' as const,
        // Защитный зажим: тянуть полосу из далёкого прошлого незачем, а
        // считать её координаты — тем более.
        from: Math.max(level.from, floor),
        to: level.to,
        low: level.price - half,
        high: level.price + half,
        alpha: fillAlpha(
          alphaOf(level.power, alive),
          numberParam(p, 'liquidityOpacity', FILL_TRANSPARENCY),
        ),
        tone: (level.side === 'above' ? 'down' : 'up') as 'up' | 'down',
        color: level.side === 'above' ? above : below,
      };
    });
}

/** Ближайшая неснятая ликвидность по обе стороны от цены. */
function nearestLiquidity(
  candles: readonly Candle[],
  p: IndicatorParams,
): { above: number | null; below: number | null } {
  const last = candles[candles.length - 1];
  if (!last) return { above: null, below: null };

  let above: number | null = null;
  let below: number | null = null;

  // Снятые не считаются: за ними уже сходили, и ждать там больше нечего.
  for (const level of liquidityMap(candles, {
    levels: numberParam(p, 'levels', 400),
    age: numberParam(p, 'age', 2500),
  })) {
    if (level.to !== null || level.evictedAt !== null) continue;
    if (level.price > last.close && (above === null || level.price < above)) above = level.price;
    if (level.price < last.close && (below === null || level.price > below)) below = level.price;
  }

  return { above, below };
}

/** Настройки карты ликвидности — те же, что у отдельного индикатора. */
const LIQUIDITY_PARAMS: readonly IndicatorParamSpec[] = [
  {
    key: 'levels',
    label: 'Максимум уровней на карте',
    hint:
      'Сколько уровней держать на графике разом. Больше — полнее картина, но карта превращается в частокол.',
    type: 'number',
    default: 400,
    min: 5,
    max: 400,
    step: 5,
  },
  {
    key: 'age',
    label: 'Максимальный возраст уровня, свечей',
    hint:
      'Сколько свечей уровень помнится. Старый цена обычно уже забрала, и держать его — засорять карту.',
    type: 'number',
    default: 2500,
    min: 50,
    max: 5000,
    step: 50,
  },
  { key: 'below', label: 'Цвет ликвидности снизу', hint: 'Каким цветом рисовать скопления под ценой — те стопы, за которыми цена может сходить вниз.', type: 'color', default: '#00C878' },
  { key: 'above', label: 'Цвет ликвидности сверху', hint: 'Каким цветом рисовать скопления над ценой — то, за чем цена может сходить вверх.', type: 'color', default: '#FF3C50' },
  LIQUIDITY_OPACITY,
];

/** Настройки профиля объёма — те же, что у отдельного индикатора. */
const PROFILE_PARAMS: readonly IndicatorParamSpec[] = [
  {
    key: 'window',
    label: 'Окно профиля, свечей',
    hint:
      'За какой отрезок собирается профиль. Больше — видно уровни, набранные давно; меньше — только то, что торгуют сейчас.',
    type: 'number',
    default: 300,
    min: 50,
    max: 1500,
    step: 10,
  },
  { key: 'bins', label: 'Количество корзин', hint: 'На сколько ценовых полос делится размах. Больше корзин — тоньше полосы и точнее уровень, но картинка дробится.', type: 'number', default: 48, min: 10, max: 100, step: 1 },
  {
    key: 'length',
    label: 'Длина полос, свечей',
    hint:
      'Насколько далеко влево тянутся полосы. Это про вид, а не про расчёт: сам профиль от этого не меняется.',
    type: 'number',
    default: 140,
    min: 20,
    max: 500,
    step: 10,
  },
  {
    key: 'threshold',
    label: 'Порог отображения, % от максимума',
    hint:
      'Слабые полосы не рисуются вовсе. Ноль покажет все, включая пустые уровни; выше — останутся только заметные скопления.',
    type: 'number',
    default: 12,
    min: 1,
    max: 90,
    step: 1,
  },
  PROFILE_OPACITY,
];

/**
 * Счёт сигнала по текущему бару.
 *
 * Собирает вход из того же, что уже посчитано для картинки: зоны, уровни
 * карты, ранги таймфреймов. Второй путь расчёта дал бы отметку, не
 * совпадающую с тем, что на экране, — и понять, какая из двух права,
 * было бы нечем.
 */
function signals(
  candles: readonly Candle[],
  p: IndicatorParams,
  context: IndicatorContext,
) {
  return zonesProState(
    {
      candles,
      frame: (tf) => context.series(tf as Timeframe),
      openInterest: context.derivative('oi'),
      premium: context.derivative('premium'),
      funding: context.derivative('funding'),
      usdtd: context.index('USDT.D'),
      livePartial: context.livePartial,
    },
    zonesProOptions(p),
    shapeOf(p),
  );
}

/**
 * Слепок настроек для подписки на уведомления.
 *
 * Тем же составом сервер считает сигнал при закрытой вкладке. Слепок, а
 * не ссылка на индикатор: индикатор человек правит, инструмент меняет,
 * а подписка обязана оставаться той, которую включали, пока её не
 * переписали намеренно.
 */
export function zonesWatchSnapshot(p: IndicatorParams): {
  readonly options: SignalOptions;
  readonly shape: ZonesProShape;
} {
  return { options: zonesProOptions(p), shape: shapeOf(p) };
}

/** Настройки счёта из параметров индикатора. */
function zonesProOptions(p: IndicatorParams): SignalOptions {
  return {
    threshold: numberParam(p, 'minScore', 90),
    closedOnly: boolParam(p, 'closedOnly', true),
    requireOi: boolParam(p, 'requireOi', true),
    usdtd: { on: boolParam(p, 'usdtd', true), weight: numberParam(p, 'usdtdWeight', 10) },
    premium: {
      on: boolParam(p, 'premium', true),
      weight: numberParam(p, 'premiumWeight', 10),
    },
    funding: {
      on: boolParam(p, 'funding', true),
      weight: numberParam(p, 'fundingWeight', 5),
    },
  };
}

/**
 * Выгодные зоны PRO.
 *
 * Целый индикатор, а не одна величина: зоны «дёшево» и «дорого», карта
 * ликвидности и профиль объёма на одном экране, плюс табло с ответом
 * словами. Собран под один вопрос — стоит ли покупать здесь, — и каждая
 * часть отвечает на свою половину: зона говорит «дёшево ли», карта —
 * «есть ли куда идти», профиль — «где рынок стоял».
 *
 * Наружу вынесено только то, что меняет вид: две галочки и настройки
 * карты с профилем. Пороги зон, сглаживание, проценты и уровни RSI —
 * состав индикатора; открыв их, мы отдали бы рецепт, а не удобство.
 */
/**
 * Ключ «Выгодных зон PRO».
 *
 * Назван постоянной, потому что по нему проверяют не только реестр:
 * панель настроек показывает у этого индикатора выключатель
 * уведомлений, а сервер считает по нему подписки. Строка, набранная в
 * трёх местах руками, однажды разойдётся в одном из них.
 */
export const ZONES_PRO_ID = 'zones-pro';

const ZONES_PRO: IndicatorDefinition = {
  id: ZONES_PRO_ID,
  name: 'Выгодные зоны PRO',
  description:
    'Дёшево и дорого относительно недавней истории: три вложенные зоны на сторону, ' +
    'карта ликвидности и профиль объёма. Табло отвечает словами.',
  author: 'specter',
  placement: 'overlay',
  params: [
    {
      /*
       * Ключ не `threshold`: так же называется порог отображения у
       * профиля объёма, и в одном списке настроек они затёрли бы друг
       * друга. Настройки индикатора живут в общем словаре, и одинаковое
       * имя означает одно значение на двоих.
       */
      key: 'minScore',
      label: 'Порог сигнала',
      hint:
        'Слабее этого счёта отметка не ставится и уведомление не уходит. Выше порог — сигналов меньше, но каждый весомее.',
      type: 'number',
      default: 90,
      min: 10,
      max: 100,
      step: 5,
    },
    { key: 'closedOnly', label: 'Только по закрытой свече', hint: 'Сигнал ставится на закрытии свечи. Если выключить, он может появиться внутри свечи и исчезнуть: цена ушла, и основания не стало.', type: 'boolean', default: true },
    {
      /*
       * Единственная настройка сверх названных: у неё нет разумного
       * умолчания за человека. Включённая — сигналов меньше, но каждый
       * проверен целиком; выключенная — видно всю историю, но глубже
       * тридцати суток счёт собран без открытого интереса.
       */
      key: 'requireOi',
      label: 'Без данных OI сигналов не давать',
      hint:
        'Открытый интерес весит в счёте много. Когда биржа его не отдаёт, счёт собирается из остального и выходит завышенным — честнее промолчать.',
      type: 'boolean',
      default: true,
    },
    /*
     * Три надбавки выключены по умолчанию.
     *
     * Каждая прибавляет очки поверх счёта, собранного по восьми блокам,
     * и потому сдвигает порог: с ними до девяноста дотягивают входы,
     * которые сам расчёт оценил слабее. Это осознанный выбор человека
     * «добавить ещё один довод», а не то, с чего разумно начинать
     * знакомство с индикатором.
     *
     * У двух из них к тому же нет данных с первого дня: доминирование
     * USDT копится с подключения Core, премия у биржи хранится
     * ограниченный срок. Включённый фильтр без ленты молчит — и это
     * читается как «рынок нейтрален», хотя означает «данных нет».
     */
    { key: 'usdtd', label: 'USDT.D фильтр', hint: 'Учитывать ли долю доллара в рынке. Она растёт, когда из монет уходят в стейблкоины, — то есть когда покупать опаснее.', type: 'boolean', default: false },
    { key: 'usdtdWeight', label: 'Вес USDT.D', hint: 'Сколько очков из ста даёт этот довод. Ноль — учитывать, но не влиять на счёт; больше — сильнее двигать итог.', type: 'number', default: 10, min: 0, max: 30, step: 1 },
    { key: 'premium', label: 'Премия фильтр', hint: 'Учитывать ли разницу между ценой контракта и спота. Большая премия — толпа сидит в плече, и разворот обходится дороже.', type: 'boolean', default: false },
    {
      key: 'premiumWeight',
      label: 'Вес премии',
      hint:
        'Сколько очков из ста даёт премия. Ноль — учитывать, но не влиять на счёт; больше — сильнее двигать итог.',
      type: 'number',
      default: 10,
      min: 0,
      max: 30,
      step: 1,
    },
    { key: 'funding', label: 'Real Funding фильтр', hint: 'Учитывать ли ставку финансирования — плату, которую одна сторона рынка отдаёт другой за удержание позиции.', type: 'boolean', default: false },
    {
      key: 'fundingWeight',
      label: 'Вес Real Funding',
      hint:
        'Сколько очков из ста даёт ставка. Ноль — учитывать, но не влиять на счёт; больше — сильнее двигать итог.',
      type: 'number',
      default: 5,
      min: 0,
      max: 30,
      step: 1,
    },
    { key: 'midline', label: 'Показывать среднюю линию', hint: 'Линия справедливой цены посередине зон. По ней видно, дорого сейчас или дёшево относительно недавней истории.', type: 'boolean', default: true },
    {
      /*
       * Ключ совпадает с ключом выхода — так цвет линии оказывается
       * рядом с её галочкой, а не в общем блоке цветов под двумя
       * десятками настроек. Реестр эту связь понимает: см. `colorOf`.
       */
      key: 'fair',
      label: 'Цвет средней линии',
      hint:
        'Каким цветом рисовать линию справедливой цены посередине зон.',
      type: 'color',
      default: '#7a8290',
    },
    { key: 'table', label: 'Показывать таблицу', hint: 'Небольшое табло в углу графика: то, что индикатор знает, но линией сказать не может.', type: 'boolean', default: true },
    {
      /*
       * Прозрачность не задаётся напрямую, а прибавляется к базовой у
       * каждой зоны: широкая всегда бледнее сильной, сильная —
       * экстремальной. Ручка двигает всю тройку разом и сохраняет между
       * ними разницу, иначе на одном значении они слились бы в пятно.
       */
      key: 'opacity',
      label: 'Прозрачность заливки зон, %',
      hint:
        'Насколько густо залиты зоны. Больше процент — бледнее, и свечи под ними видно лучше.',
      type: 'number',
      default: 70,
      min: 0,
      max: 99,
      step: 5,
    },
    /*
     * Карта и профиль выключены по умолчанию: оба рисуют десятки полос
     * поверх свечей. На счёт ликвидность влияет всегда — блок считается
     * независимо от галочки, она про то, видно ли уровни на графике.
     */
    { key: 'liquidity', label: 'Карта ликвидности', hint: 'Показывать ли скопления стоп-заявок над ценой и под ней. Считаются они в любом случае — галочка только про показ.', type: 'boolean', default: false },
    ...LIQUIDITY_PARAMS,
    { key: 'profile', label: 'Профиль объёма', hint: 'Показывать ли, на каких ценах наторговали больше всего. Полосы слева — это объём, а не время.', type: 'boolean', default: false },
    ...PROFILE_PARAMS,
  ],
  /*
   * Средняя линия — обычная серия, а не фигура.
   *
   * У неё есть значение на каждом баре, то есть ровно то, чем серия и
   * является. Рисовать её слоем фигур значило бы заводить второй способ
   * делать то, что реестр умеет с самого начала, — и отдельно чинить его
   * при каждой правке графика.
   */
  outputs: [
    {
      key: 'fair',
      label: 'Средняя линия',
      type: 'line',
      defaultColor: '#7a8290',
      lineWidth: 1,
    },
  ],
  // Открытый интерес, ставка и премия приходят своими лентами от Core:
  // свечами их не выразить. Доминирование USDT — рыночный агрегат.
  derivatives: ['oi', 'funding', 'premium'],
  indices: ['USDT.D'],
  // Ранг по таймфреймам считается по их собственным рядам, а не по
  // пересобранным: см. `use-indicator-series.ts`.
  timeframes: ZONE_READOUT_TIMEFRAMES.map((frame) => frame.timeframe),
  warmup: () => ZONE_WARMUP,
  calc: (chartCandles, p, context) => {
    // Выключенная линия — пустой ряд, а не отсутствующий выход: реестр
    // ждёт выход всегда, а рисовать в нём нечего.
    if (!boolParam(p, 'midline', true)) return { fair: [] };

    const candles = deepen(chartCandles, context);
    const series = analyse(candles, p).zones;

    /*
     * Линия обрезается по тому, что загружено на графике.
     *
     * Считается она по глубокому ряду — иначе средней не из чего взяться
     * на коротком графике. Но отдать её целиком нельзя: серия с точками
     * за февраль растягивает шкалу времени графика под себя, а слой
     * фигур считает координаты по загруженным свечам — и зоны с картой
     * уезжают в никуда. Так я их однажды и потерял: линия появилась,
     * всё остальное исчезло.
     */
    const edge = chartCandles[0]?.time ?? 0;

    // Медиана выборки: цена, вокруг которой рынок реально торговался.
    return {
      fair: series.time
        .map((time, i) => ({ time, value: series.fairValue[i] ?? null }))
        .filter((point) => point.time >= edge),
    };
  },
  shapes: (chartCandles, p, context) => {
    if (chartCandles.length === 0) return [];

    const candles = deepen(chartCandles, context);
    const series = analyse(candles, p).zones;
    const shapes: IndicatorShape[] = [];

    /*
     * Зона — заливка между двумя границами, а не прямоугольник: её края
     * едут вместе с рынком. Три вложенные полосы на сторону дают
     * градиент сами по себе — чем глубже, тем плотнее, — и отдельная
     * подпись «экстремальная» становится не нужна.
     */
    const opacity = numberParam(p, 'opacity', 70);

    const band = (
      upper: readonly (number | null)[],
      lower: readonly (number | null)[],
      tone: 'up' | 'down',
      base: number,
    ): void => {
      const points: Array<{ time: number; upper: number; lower: number }> = [];
      for (let i = 0; i < series.time.length; i += 1) {
        const top = upper[i];
        const bottom = lower[i];
        if (top == null || bottom == null) continue;
        points.push({ time: series.time[i]!, upper: top, lower: bottom });
      }
      if (points.length > 1) {
        shapes.push({ kind: 'band', points, alpha: zoneAlpha(base, opacity), tone });
      }
    };

    band(series.buyOuterTop, series.buyOuterBottom, 'up', ZONE_FADE.outer);
    band(series.buyStrongTop, series.buyStrongBottom, 'up', ZONE_FADE.strong);
    band(series.buyExtremeTop, series.buyExtremeBottom, 'up', ZONE_FADE.extreme);
    band(series.sellOuterTop, series.sellOuterBottom, 'down', ZONE_FADE.outer);
    band(series.sellStrongTop, series.sellStrongBottom, 'down', ZONE_FADE.strong);
    band(series.sellExtremeTop, series.sellExtremeBottom, 'down', ZONE_FADE.extreme);

    if (boolParam(p, 'liquidity', false)) shapes.push(...liquidityShapes(candles, p));
    if (boolParam(p, 'profile', false)) shapes.push(...profileShapes(candles, p));

    /*
     * Сигнал рисуется отметкой, а не строкой в табло.
     *
     * Счёт — величина момента: он живёт на конкретном баре и вместе с
     * ним уходит. Числу в табло человек поверил бы как состоянию, тогда
     * как оно относится к одной свече.
     */
    /*
     * Метки — только по полным данным.
     *
     * Открытый интерес весит сорок очков из ста, и пока его ряд едет,
     * счёт считается по оставшимся шестидесяти с пересчётом к сотне —
     * то есть завышенным. Нарисовать метку сейчас и убрать через
     * секунду значит показать сигнал, которого не было.
     */
    for (const mark of context.derivativesReady ? signals(candles, p, context).marks : []) {
      shapes.push({
        kind: 'mark',
        time: mark.time,
        price: mark.price,
        tone: mark.side === 'buy' ? 'up' : 'down',
        text: String(Math.round(mark.score)),
      });
    }

    return shapes;
  },
  readout: (chartCandles, p, context) => {
    if (!boolParam(p, 'table', true)) return [];

    const candles = deepen(chartCandles, context);
    const last = candles[candles.length - 1];
    if (!last) return [];

    const reading = zoneReading(candles, analyse(candles, p).zones);
    const score = signals(candles, p, context);

    const rows: ReadoutRow[] = [
      {
        label: 'Зона',
        value: reading.label,
        tone: reading.side === 'buy' ? 'up' : reading.side === 'sell' ? 'down' : 'muted',
      },
      {
        // Глубина показывается только для той стороны, в зоне которой
        // стоим: ноль вне зоны прочитался бы как «на самом краю».
        label: 'Глубина в зоне',
        value: reading.depth === null ? '—' : `${Math.round(reading.depth)}%`,
        tone: reading.depth === null ? 'muted' : undefined,
      },
      {
        /*
         * Обе стороны рядом, а не только выигравшая.
         *
         * Счёт сам по себе мало что говорит: восемьдесят у лонга при
         * семидесяти девяти у шорта — это спорный рынок, а восемьдесят
         * против нуля — согласный. Одно число вместо двух прячет ровно
         * эту разницу.
         */
        label: 'Счёт лонг / шорт',
        value: `${Math.round(score.buyScore)} / ${Math.round(score.sellScore)}`,
        tone:
          score.buyScore === 0 && score.sellScore === 0
            ? 'muted'
            : score.buyScore > score.sellScore
              ? 'up'
              : score.sellScore > score.buyScore
                ? 'down'
                : undefined,
      },
    ];

    /*
     * С какой даты вообще есть открытый интерес.
     *
     * Без этой строки поведение индикатора выглядит необъяснимым.
     * Открытый интерес весит сорок очков из ста, и там, где ряд до бара
     * не дотянулся, оба его блока выпадают из знаменателя: счёт
     * считается по остальным шестидесяти и нормируется к сотне. Метки
     * по обе стороны от этой даты — разного качества, а на графике они
     * выглядят одинаково.
     *
     * У Binance ряд живёт около тридцати суток, у Bybit — годы; вдобавок
     * глубина запроса растёт по мере подгрузки истории. Поэтому граница
     * не постоянная, и назвать её может только сам индикатор.
     */
    {
      const oi = context.derivative('oi');
      const from = oi[0]?.at ?? null;

      rows.push({
        label: 'Интерес с',
        value:
          from === null
            ? 'нет данных'
            : new Date(from).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' }),
        tone: from === null ? 'muted' : undefined,
      });
    }

    /*
     * Расстояние до ликвидности показывается всегда, а не по галочке
     * карты.
     *
     * Галочка включает полосы на графике — то есть вид. Уровни при этом
     * считаются в любом случае: на них стоит целый блок счёта. Прятать
     * готовое число вместе с полосами значило бы отвечать видом на
     * вопрос о данных.
     */
    {
      const { above, below } = nearestLiquidity(candles, p);

      const up =
        above === null ? '—' : `↑ ${(((above - last.close) / last.close) * 100).toFixed(2)}%`;
      const down =
        below === null ? '—' : `↓ ${(((last.close - below) / last.close) * 100).toFixed(2)}%`;

      rows.push({
        label: 'Ликвидность',
        value: `${up}   ${down}`,
        tone: above === null && below === null ? 'muted' : undefined,
      });
    }

    /*
     * Ранг цены по таймфреймам: одно число, которое одинаково считается
     * на любом из них и потому годится для сравнения. Сами границы зон у
     * разных таймфреймов стоят в разных ценах и сравнению не поддаются.
     */
    for (const frame of timeframeRanks(candles, stepOf(candles), resample, (tf) =>
      context.series(tf as Timeframe),
    )) {
      /*
       * Прочерк без причины читается как поломка. Младший таймфрейм не
       * появится никогда — его не из чего собрать; короткий ряд
       * дозагрузится прокруткой. Это разные ответы.
       */
      const value =
        frame.rank !== null
          ? `${Math.round(frame.rank)}%`
          : frame.missing === 'younger'
            ? 'младше графика'
            : 'мало истории';

      rows.push({
        label: frame.label,
        value,
        /*
         * Цвет по тем же границам, по которым строятся зоны: ниже
         * `cheap` цена в дешёвой части выборки, выше `rich` — в
         * дорогой. Между ними ранг ничего не советует, и красить его
         * значило бы подсказывать там, где подсказки нет.
         */
        tone:
          frame.rank === null
            ? 'muted'
            : frame.rank <= RANK_ZONES.cheap
              ? 'up'
              : frame.rank >= RANK_ZONES.rich
                ? 'down'
                : undefined,
        ...(frame.rank === null ? {} : { meter: frame.rank }),
      });
    }

    return rows;
  },
  label: () => 'Выгодные зоны PRO',
};

// --- Spot CVD ---------------------------------------------------------------

const DAY_MS = 86_400_000;

/**
 * Линия CVD и столбцы дельты по барам графика.
 *
 * Отдельной функцией, а не внутри определения: её и проверяют отдельно —
 * накопление, сброс, пропуски.
 *
 * Бар без дельты — пустое место на линии, а не ровный отрезок: ровная
 * линия означала бы «перевеса не было», а на деле данных просто нет.
 */
export function spotCvdSeries(
  candles: readonly Candle[],
  deltas: readonly DerivativePoint[],
  anchor: string,
  bars: boolean,
): { readonly cvd: IndicatorPoint[]; readonly delta: IndicatorPoint[] } {
  const byTime = new Map(deltas.map((point) => [point.at, point.value]));
  const cvd: IndicatorPoint[] = [];
  const delta: IndicatorPoint[] = [];

  let sum: number | null = null;
  let period: number | null = null;

  for (const candle of candles) {
    const key =
      anchor === 'day'
        ? Math.floor(candle.time / DAY_MS)
        : anchor === 'week'
          ? alignToTimeframe(candle.time, '1W')
          : 0;
    if (period !== key) {
      period = key;
      // Новый день или неделя — счёт с нуля; до первых данных — пусто.
      if (sum !== null) sum = 0;
    }

    const value = byTime.get(candle.time);
    if (value === undefined) {
      cvd.push({ time: candle.time, value: null });
      delta.push({ time: candle.time, value: null });
      continue;
    }

    sum = (sum ?? 0) + value;
    cvd.push({ time: candle.time, value: sum });
    /*
     * Выключенные столбцы — пустые значения на каждом баре, а не пустой
     * ряд: график ждёт выходы, выровненные по барам, и ряд короче свечей
     * сдвинул бы всё, что рисуется после него.
     */
    delta.push(
      bars
        ? { time: candle.time, value, color: value >= 0 ? VOLUME_UP : VOLUME_DOWN }
        : { time: candle.time, value: null },
    );
  }

  return { cvd, delta };
}

/** Выводы CVD: тот же разбор, что у «Дельты по сделкам», по ряду Binance. */
function cvdExplained(
  candles: readonly Candle[],
  p: IndicatorParams,
  context: IndicatorContext,
  kind: 'spotDelta' | 'futuresDelta',
) {
  const byTime = new Map(context.derivative(kind).map((point) => [point.at, point.value]));
  const anchor = typeof p['anchor'] === 'string' ? p['anchor'] : 'none';
  return explainFlow(
    candles,
    candles.map((candle) => byTime.get(candle.time) ?? null),
    anchor === 'day' ? 'day' : anchor === 'week' ? 'week' : 'all',
  );
}

/**
 * Spot CVD — накопленный перевес покупок над продажами «по рынку».
 *
 * У каждой сделки есть инициатор: тот, кто не стал ждать и забрал чужую
 * заявку. Его покупки минус его продажи, сложенные бар за баром, и есть
 * CVD. Линия растёт — покупают настойчивее, чем продают; падает —
 * наоборот. Главное в ней — расхождение с ценой: цена растёт, а линия
 * падает — рост идёт без покупок монет.
 *
 * Ряд — спот Binance, какая бы биржа ни стояла на графике: только он
 * отдаёт объём покупок по рынку прямо в свечах. Поэтому это написано в
 * подписи.
 *
 * Где начинать счёт, решает человек. С начала истории — линия идёт
 * через всё загруженное, удобно искать расхождения. Каждый день или
 * неделю заново — привычный вид: сколько накопилось с утра.
 */
const SPOT_CVD: IndicatorDefinition = {
  id: 'spot-cvd',
  name: 'Spot CVD',
  description: 'Перевес покупок над продажами на споте Binance, накопленный бар за баром',
  placement: 'pane',
  params: [
    {
      key: 'anchor',
      label: 'Начинать счёт',
      hint:
        'С какого момента копится перевес покупок над продажами. От начала суток привычнее всего: видно, кто побеждает сегодня.',
      type: 'select',
      default: 'none',
      options: [
        { value: 'none', label: 'С начала истории' },
        { value: 'day', label: 'Каждый день заново' },
        { value: 'week', label: 'Каждую неделю заново' },
      ],
    },
    { key: 'bars', label: 'Дельта по барам', hint: 'Показывать ли рядом с накопленной линией столбики перевеса на каждой отдельной свече.', type: 'boolean', default: false },
    {
      key: 'marks',
      label: 'Отметки на цене',
      hint: 'Треугольник там, где цена обновила максимум без новых покупок (или минимум без новых продаж) — движение может выдохнуться.',
      type: 'boolean',
      default: true,
    },
    TABLE_PARAM,
  ],
  readout: (candles, p, context) => cvdExplained(candles, p, context, 'spotDelta').rows,
  shapes: (candles, p, context) =>
    boolParam(p, 'marks', true) ? cvdExplained(candles, p, context, 'spotDelta').marks : [],
  legend: [
    {
      sample: 'soft',
      term: 'Линия — ',
      text: 'накопленный перевес покупок по рынку над продажами. Растёт — покупают настойчивее, падает — продают.',
    },
    {
      sample: 'strong',
      term: 'Треугольник на цене — ',
      text: 'расхождение: цена обновила максимум, а покупок не прибавилось (или минимум — без новых продаж). Движение может выдохнуться.',
    },
  ],
  derivatives: ['spotDelta'],
  outputs: [
    {
      key: 'cvd',
      label: 'CVD',
      type: 'line',
      defaultColor: COLORS.cyan,
      lineWidth: 2,
      format: 'money',
    },
    {
      key: 'delta',
      label: 'Дельта бара',
      type: 'histogram',
      defaultColor: COLORS.grey,
      format: 'money',
    },
  ],
  scale: { levels: [{ value: 0, color: COLORS.grey, dashed: true }] },
  warmup: () => 0,
  label: () => 'Spot CVD · Binance',
  calc: (candles, p, context) =>
    spotCvdSeries(
      candles,
      context.derivative('spotDelta'),
      typeof p['anchor'] === 'string' ? p['anchor'] : 'none',
      p['bars'] === true,
    ),
};

/**
 * Futures CVD — то же в бессрочных контрактах Binance.
 *
 * Только Binance: точную дельту на любом таймфрейме дают только его свечи
 * контрактов. Сумма по биржам есть в Market Intelligence — по пять минут и
 * на ту глубину, что держат площадки; для графика этого мало, и подпись
 * говорит, чей это ряд.
 */
const FUTURES_CVD: IndicatorDefinition = {
  ...SPOT_CVD,
  id: 'futures-cvd',
  name: 'Futures CVD',
  description: 'Перевес покупок над продажами в контрактах Binance, накопленный бар за баром',
  derivatives: ['futuresDelta'],
  outputs: [
    { ...SPOT_CVD.outputs[0]!, defaultColor: COLORS.amber },
    SPOT_CVD.outputs[1]!,
  ],
  label: () => 'Futures CVD · Binance',
  readout: (candles, p, context) => cvdExplained(candles, p, context, 'futuresDelta').rows,
  shapes: (candles, p, context) =>
    boolParam(p, 'marks', true) ? cvdExplained(candles, p, context, 'futuresDelta').marks : [],
  calc: (candles, p, context) =>
    spotCvdSeries(
      candles,
      context.derivative('futuresDelta'),
      typeof p['anchor'] === 'string' ? p['anchor'] : 'none',
      p['bars'] === true,
    ),
};

/* --------------------------------------------------------- дивергенции */

/**
 * Дивергенции: цена и поток сделок говорят разное.
 *
 * Цена ставит новый минимум, а продавать стали меньше — значит, давление
 * слабее, чем выглядит по свечам. Индикатор ищет это между ценой и тремя
 * потоками сразу: покупками монет на споте, ставками в контрактах и —
 * отдельным классом — спором спота с контрактами.
 *
 * Считает движок из `@specter/rules`: тот же, которым это может считать
 * сервер, и с теми же правилами честности — экстремум подтверждается
 * окном справа, событие появляется на баре подтверждения, пороги меряются
 * в размахах самой монеты, а не в долларах.
 *
 * На графике расхождение — линия между двумя вершинами: пунктиром у
 * скрытых, со счётом у конца и точками на самих вершинах. Отменённое
 * ценой не исчезает, а гаснет: человек должен видеть, что сигнал был и
 * чем кончился.
 */
export const DIVERGENCE_ID = 'divergence';

/**
 * Разбудит ли это расхождение при нынешних порогах.
 *
 * Тот же отбор, что у сторожа на сервере: у каждого источника своя
 * планка, скрытые — отдельной галочкой. Считается здесь затем, чтобы на
 * графике указатель стоял ровно там, где придёт уведомление. Разойдись
 * они — и человек перестанет верить обоим.
 */
function wakesUp(entry: Divergence, p: IndicatorParams): boolean {
  if (entry.kind === 'hidden' && !boolParam(p, 'signalHidden', true)) return false;

  const level =
    entry.source === 'spot'
      ? numberParam(p, 'signalSpot', 40)
      : entry.source === 'futures'
        ? numberParam(p, 'signalFutures', 40)
        : numberParam(p, 'signalFlow', 40);

  return entry.score >= level;
}

const DIVERGENCE_SETTINGS = (p: IndicatorParams): Partial<DivergenceSettings> => ({
  left: numberParam(p, 'sensitivity', 5),
  right: numberParam(p, 'sensitivity', 5),
  maxGap: numberParam(p, 'maxGap', 60),
  priceThreshold: numberParam(p, 'priceThreshold', 50) / 100,
  valueThreshold: numberParam(p, 'valueThreshold', 50) / 100,
  minScore: numberParam(p, 'minScore', 40),
  hidden: boolParam(p, 'hidden', true),
});

/**
 * Разбор ряда с памятью на один вызов.
 *
 * `shapes` и `readout` зовутся подряд с одним и тем же рядом, а разбор
 * проходит по всей истории: без памяти он считался бы дважды на каждый
 * тик.
 */
let lastDivergence: { key: string; found: Divergence[] } | null = null;

function divergencesOf(
  candles: readonly Candle[],
  p: IndicatorParams,
  context: IndicatorContext,
): Divergence[] {
  const source = selectParam(p, 'source', 'all');
  const spotDelta = context.derivative('spotDelta');
  const futuresDelta = context.derivative('futuresDelta');
  const oi = context.derivative('oi');

  const key = [
    context.instrument,
    candles.length,
    candles[candles.length - 1]?.time ?? 0,
    spotDelta.length,
    futuresDelta.length,
    oi.length,
    JSON.stringify(p),
  ].join('|');
  if (lastDivergence?.key === key) return lastDivergence.found;

  const wantsSpot = source === 'all' || source === 'spot' || source === 'flow';
  const wantsFutures = source === 'all' || source === 'futures' || source === 'flow';

  const found = divergenceScan(
    {
      bars: candles as readonly Bar[],
      ...(wantsSpot ? { spot: cumulativeOf(candles, spotDelta) } : {}),
      ...(wantsFutures ? { futures: cumulativeOf(candles, futuresDelta) } : {}),
      openInterest: alignedOf(candles, oi),
    },
    DIVERGENCE_SETTINGS(p),
  ).filter((entry) => {
    if (source === 'all') return true;
    if (source === 'flow') return entry.source === 'flow';
    return entry.source === source;
  });

  lastDivergence = { key, found };
  return found;
}

const DIVERGENCE_SIDE: Record<DivergenceSide, string> = {
  bullish: 'бычья',
  bearish: 'медвежья',
};

const DIVERGENCE_SOURCE_TITLE: Record<DivergenceSource, string> = {
  spot: 'Спот',
  futures: 'Контракты',
  flow: 'Потоки',
};

const DIVERGENCE_STATE: Record<Divergence['state'], { text: string; tone: 'bull' | 'bear' | 'warn' | 'muted' }> = {
  confirmed: { text: 'подтв.', tone: 'bull' },
  forming: { text: 'зреет', tone: 'warn' },
  invalidated: { text: 'отменена', tone: 'muted' },
};

const DIVERGENCE_POSITIONING: Record<string, string> = {
  'new-longs': 'новые ставки на рост',
  'new-shorts': 'новые ставки на падение',
  'short-covering': 'закрытие ставок на падение',
  'long-exit': 'закрытие ставок на рост',
  flat: 'интерес на месте',
};

/**
 * Давность расхождения для табло.
 *
 * Коротко до предела: строка узкая, и «девять суток назад» съест место,
 * которое нужно виду и стороне. Точность тут и не требуется — вопрос, на
 * который отвечает подпись, звучит как «это сейчас или давно».
 *
 * Нужна она вот зачем. В таблице три строки, по одной на источник, и
 * стоят они рядом как одна картина. А на деле расхождение потоков
 * запросто оказывается недельной давности, когда спот и контракты —
 * шестичасовой: измерено на девяноста сутках часового BTC, 226 часов
 * против шести. Понять это по таблице было нечем.
 */
export function agoShort(ms: number): string {
  const minutes = Math.max(0, Math.round(ms / 60_000));
  if (minutes < 60) return `${minutes} мин`;

  const hours = Math.round(minutes / 60);
  // Двое суток в часах — ещё осмысленный ответ; трое уже нет.
  if (hours < 48) return `${hours} ч`;

  return `${Math.round(hours / 24)} сут`;
}

/*
 * Как читать линии расхождения.
 *
 * Два признака, и оба немые. Форма отвечает на «про что это»: сплошная —
 * про разворот, пунктир — про продолжение. Яркость отвечает на «верить ли
 * уже»: подтверждённое горит, зреющее приглушено, отменённое гаснет.
 * Человек видит и то и другое сразу, но догадаться об этом неоткуда, а
 * ошибка дорогая: пунктир, принятый за разворот, означает вход против
 * тренда.
 *
 * Доли насыщенности здесь те же, что в расчёте фигур ниже. Разойдутся —
 * образец начнёт врать, и лучше бы его тогда не было вовсе.
 */
const DIVERGENCE_LEGEND: readonly ReadoutLegendItem[] = [
  {
    sample: 'solid',
    term: 'Сплошная — классическая',
    text: 'Про возможный разворот. Цена поставила новый максимум, а поток за ней не пошёл: покупатели выдыхаются. Или новый минимум без продолжения в потоке: продавцы давят слабее, чем выглядит по цене.',
  },
  {
    sample: 'dashed',
    term: 'Пунктир — скрытая',
    text: 'Про продолжение текущего движения. Цена откатилась неглубоко, а поток сходил дальше: коррекция внутри тренда, а не его конец.',
  },
  {
    sample: 'strong',
    term: 'Яркая, со свечением',
    text: 'Подтверждена: обе вершины закрылись, расхождение состоялось. Точки на концах крупнее, линия толще.',
  },
  {
    sample: 'soft',
    term: 'Приглушённая',
    text: 'Формируется: вторая вершина ещё не подтверждена и может не состояться.',
  },
  {
    sample: 'faded',
    term: 'Почти погасшая',
    text: 'Отменена: цена сходила туда, куда расхождение не пускало. Висит заданное настройкой число свечей как память и гаснет.',
  },
];

const DIVERGENCE: IndicatorDefinition = {
  id: DIVERGENCE_ID,
  name: 'Дивергенции',
  description:
    'Расхождения цены с потоком сделок: спот, контракты и спор между ними. ' +
    'Линия между вершинами, счёт качества и таблица словами.',
  author: 'specter',
  placement: 'overlay',
  derivatives: ['spotDelta', 'futuresDelta', 'oi'],
  params: [
    {
      key: 'source',
      label: 'Что сравнивать',
      hint:
        'С чем сверять цену: с покупками монет на споте, со ставками в контрактах — или со спором этих двух между собой.',
      type: 'select',
      default: 'all',
      options: [
        { value: 'all', label: 'Всё сразу' },
        { value: 'spot', label: 'Цена и Spot CVD' },
        { value: 'futures', label: 'Цена и Futures CVD' },
        { value: 'flow', label: 'Спот против контрактов' },
      ],
    },
    {
      /*
       * Одно число на оба окна: слева и справа.
       *
       * Разводить их врозь значит отдать человеку настройку, у которой
       * нет понятного смысла: окно подтверждения тем и определено, что
       * симметрично вершине.
       */
      key: 'sensitivity',
      label: 'Окно вершины, свечей',
      hint:
        'Сколько свечей должно пройти после вершины, чтобы её признать. Меньше — сигнал раньше, но чаще ложный: вершиной окажется любой откат. Единица — самое быстрое: вершину признаём на следующей же свече. Нуля нет: без единой свечи справа вершиной оказывается вообще каждая.',
      type: 'number',
      default: 5,
      min: 1,
      max: 30,
      step: 1,
    },
    {
      key: 'maxGap',
      label: 'Дальше этого вершины не пара, свечей',
      hint:
        'Насколько далеко друг от друга могут стоять две вершины. Слишком далёкие — уже не одно движение, и сравнивать их нечестно.',
      type: 'number',
      default: 60,
      min: 10,
      max: 300,
      step: 5,
    },
    {
      key: 'priceThreshold',
      label: 'Порог по цене, % от обычного размаха',
      hint:
        'Насколько цена должна отойти, чтобы это считалось движением. Меряется в её же обычном размахе, а не в долларах, — поэтому работает одинаково на любой монете.',
      type: 'number',
      default: 50,
      min: 10,
      max: 300,
      step: 10,
    },
    {
      key: 'valueThreshold',
      label: 'Порог по потоку, % от его разброса',
      hint:
        'Насколько должен разойтись поток, чтобы это считалось расхождением. Тоже в своих единицах: у каждой монеты свой обычный разброс.',
      type: 'number',
      default: 50,
      min: 10,
      max: 300,
      step: 10,
    },
    {
      key: 'minScore',
      label: 'Показывать от силы',
      hint:
        'Слабее этого расхождения на график не попадают. Выше порог — остаются только заметные, ниже — видно всё, включая мелочь.',
      type: 'number',
      default: 40,
      min: 0,
      max: 100,
      step: 5,
    },
    { key: 'hidden', label: 'Скрытые дивергенции', hint: 'Показывать ли расхождения о продолжении хода — те, что рисуются пунктиром. Выключите, если нужны только развороты.', type: 'boolean', default: true },
    {
      /*
       * О чём уведомлять, а не что рисовать.
       *
       * Зреющее расхождение видно на графике всегда — приглушённой
       * линией. Но будить им человека по умолчанию нельзя: вторая
       * вершина ещё не подтверждена, и часть таких расхождений не
       * состоится. Сообщение, за которым ничего не следует, быстро
       * перестают читать, и вместе с ним перестают читать остальные.
       */
      key: 'notifyForming',
      label: 'Уведомлять и о зреющих',
      hint:
        'Будить ли на расхождениях, у которых вторая вершина ещё не подтверждена. Приходит раньше, но часть таких не состоится.',
      type: 'boolean',
      default: false,
    },
    {
      /*
       * Планка у каждого источника своя, и это не прихоть.
       *
       * Измерено на девяноста сутках часового BTC — доля случаев, когда
       * цена прошла хотя бы средний размах в сторону расхождения и
       * больше, чем против: контракты 56 из ста, спот 53, спор потоков
       * 29. У последнего цена идёт полразмаха в его сторону и почти
       * размах против.
       *
       * Поэтому не «включить или выключить», а порог: шумному источнику
       * поднимают планку, и он молчит на мелочи, но скажет о крупном.
       */
      key: 'signalSpot',
      label: 'Сигналить: спот от силы',
      hint:
        'С какой силы будить расхождением цены с покупками монет. Ниже порог — сигналов больше; сто — источник практически замолкает.',
      type: 'number',
      default: 40,
      min: 0,
      max: 100,
      step: 5,
    },
    {
      key: 'signalFutures',
      label: 'Сигналить: контракты от силы',
      hint:
        'С какой силы будить расхождением цены со ставками в контрактах. По истории это лучший из трёх источников — толк у 56 из ста.',
      type: 'number',
      default: 40,
      min: 0,
      max: 100,
      step: 5,
    },
    {
      key: 'signalFlow',
      label: 'Сигналить: потоки от силы',
      hint:
        'С какой силы будить спором спота с контрактами. Худший источник: толк у 29 из ста, и цена чаще идёт против — планку стоит держать выше.',
      type: 'number',
      default: 40,
      min: 0,
      max: 100,
      step: 5,
    },
    {
      key: 'signalHidden',
      label: 'Сигналить и по скрытым',
      hint:
        'Будить ли расхождениями о продолжении хода. Порогом их не отделить — они считаются той же меркой, а отменяется девять из десяти.',
      type: 'boolean',
      default: true,
    },
    {
      /*
       * Пределы показа, а не расчёта.
       *
       * Движок находит расхождения за всю загруженную историю, и на
       * экране их набирался десяток: живые тонули среди отменённых.
       * Считается по-прежнему всё — на график попадают последние.
       *
       * Двадцать — не с потолка. За девяносто суток часового BTC живых
       * расхождений пятнадцать, и двадцать показывают всю загруженную
       * историю целиком — семнадцатью линиями вместо семидесяти двух без
       * пределов. Стоявшие здесь шесть обрезали её до десяти суток:
       * история выглядела короткой там, где она есть.
       */
      key: 'maxLive',
      label: 'Сколько живых показывать',
      hint:
        'Сколько ещё не отменённых расхождений держать на графике. Больше — видно дальше в прошлое, меньше — чище картинка.',
      type: 'number',
      default: 20,
      min: 1,
      max: 60,
      step: 1,
    },
    {
      /*
       * Счётом, а не окном в свечах.
       *
       * Прежние «сто двадцать свечей» на часовом означали пять суток, а
       * на дневном — четыре месяца: одно и то же число значило на разных
       * таймфреймах разное, и человеку приходилось пересчитывать его в
       * уме под каждый график.
       *
       * Но хуже была не путаница, а обрезанная история. На графике в
       * полторы тысячи баров из тридцати восьми отменённых расхождений
       * показывалось два: всё, что не состоялось раньше пяти суток
       * назад, исчезало. Индикатор выглядел точнее, чем он есть, —
       * промахи стирались сами собой, и судить о нём было не по чему.
       *
       * Ноль прячет отменённые совсем. Это выбор человека, а не
       * умолчание: по умолчанию видно и то, что не сбылось.
       */
      key: 'maxDead',
      label: 'Сколько отменённых показывать',
      hint:
        'Сколько несбывшихся расхождений оставлять. Ноль спрячет промахи — и индикатор будет выглядеть точнее, чем он есть на самом деле.',
      type: 'number',
      default: 40,
      min: 0,
      max: 200,
      step: 5,
    },
    {
      key: 'table',
      label: 'Показывать таблицу',
      hint:
        'Небольшое табло в углу графика: то, что индикатор знает, но линией сказать не может.',
      type: 'boolean',
      default: true,
    },
    { key: 'flowColor', label: 'Цвет расхождения потоков', hint: 'Каким цветом рисовать спор спота с контрактами: это отдельный вид расхождения, и цветом он отличается тоже.', type: 'color', default: COLORS.cyan },
  ],
  outputs: [],
  warmup: (p) => numberParam(p, 'sensitivity', 5) * 2 + 50,
  calc: () => ({}),
  shapes: (candles, p, context) => {
    if (candles.length === 0 || !context.derivativesReady) return [];

    const shapes: IndicatorShape[] = [];
    const flowColor = colorParam(p, 'flowColor', COLORS.cyan);
    const all = divergencesOf(candles, p, context);

    /*
     * На графике — последние, а не все, и тех и других поровну считаем.
     *
     * У живых и у отменённых свой предел, и оба — счётом. Отменённые не
     * убираются «по старости»: они и есть история, а история, из которой
     * вычеркнуты промахи, врёт в пользу индикатора.
     *
     * Отменённые идут первыми — чтобы живые линии рисовались поверх них,
     * а не наоборот.
     */
    /** Свеча по времени: указателю нужен её низ или верх. */
    const barsAt = new Map(candles.map((candle) => [candle.time, candle]));

    const maxDead = numberParam(p, 'maxDead', 40);
    const dead =
      maxDead <= 0
        ? []
        : all.filter((entry) => entry.state === 'invalidated').slice(-maxDead);
    const alive = all
      .filter((entry) => entry.state !== 'invalidated')
      .slice(-numberParam(p, 'maxLive', 20));

    for (const entry of [...dead, ...alive]) {
      const tone = entry.side === 'bullish' ? ('up' as const) : ('down' as const);
      // Отменённое гаснет, зреющее бледнее подтверждённого: состояние
      // читается насыщенностью раньше, чем подписью.
      const alpha = entry.state === 'invalidated' ? 0.28 : entry.state === 'forming' ? 0.6 : 1;
      const color = entry.source === 'flow' ? flowColor : undefined;

      shapes.push({
        kind: 'line',
        from: { time: entry.from.at, price: entry.from.price },
        to: { time: entry.to.at, price: entry.to.price },
        tone,
        ...(color ? { color } : {}),
        dashed: entry.kind === 'hidden',
        glow: entry.state === 'confirmed',
        width: entry.state === 'confirmed' ? 1.8 : 1.4,
        alpha,
        label: `${DIVERGENCE_SOURCE_TITLE[entry.source]} ${entry.score}`,
      });

      for (const pivot of [entry.from, entry.to]) {
        shapes.push({
          kind: 'dot',
          time: pivot.at,
          price: pivot.price,
          tone,
          ...(color ? { color } : {}),
          radius: entry.state === 'confirmed' ? 3.5 : 2.5,
          alpha,
        });
      }

      /*
       * Указатель события — на баре, где расхождение состоялось.
       *
       * Линия отвечает на «что разошлось», указатель — на «когда входить
       * и в какую сторону». Это разные вопросы, и одной линией они не
       * отвечаются: её конец стоит на вершине, а событие случается позже,
       * когда вершина подтвердилась.
       *
       * Ставится ровно там, где сработает уведомление: те же пороги, что
       * у сторожа на сервере. Зреющие не в счёт — события ещё не было.
       */
      if (entry.state !== 'forming' && wakesUp(entry, p)) {
        const bar = barsAt.get(entry.signalAt);
        if (bar) {
          shapes.push({
            kind: 'pointer',
            time: entry.signalAt,
            price: entry.side === 'bullish' ? bar.low : bar.high,
            tone,
            text: `${entry.side === 'bullish' ? 'LONG' : 'SHORT'} ${entry.score}`,
            strong: entry.score >= 70,
          });
        }
      }
    }

    return shapes;
  },
  readout: (candles, p, context) => {
    if (!boolParam(p, 'table', true) || candles.length === 0) return [];

    // Заголовок табло и так называет индикатор: повторять «Дивергенции»
    // строкой ниже — тратить строку в панели шириной с ладонь.
    if (!context.derivativesReady) {
      return [{ label: 'Потоки', value: 'едут…', tone: 'muted' as const }];
    }

    const all = divergencesOf(candles, p, context);
    const live = divergenceNow(all);

    if (live.length === 0) {
      return [
        {
          label: 'Что происходит',
          value: 'Живых расхождений цены с потоками сейчас нет — цена и деньги идут в одну сторону. Индикатор молчит, и это нормально.',
          full: true,
        },
        { label: 'Живых', value: 'нет', tone: 'muted' as const },
        {
          label: 'Найдено за историю',
          value: String(all.length),
          tone: all.length === 0 ? ('muted' as const) : undefined,
        },
      ];
    }

    const rows: ReadoutRow[] = [];

    /*
     * Давность считается от последнего бара, а не от часов на стене:
     * табло описывает график, и на выходных оно обязано говорить то же,
     * что показывает цена.
     */
    const nowAt = candles[candles.length - 1]!.time;

    for (const entry of live.slice(0, 3)) {
      const state = DIVERGENCE_STATE[entry.state];
      rows.push({
        label: DIVERGENCE_SOURCE_TITLE[entry.source],
        value:
          `${entry.kind === 'hidden' ? 'скрытая ' : ''}${DIVERGENCE_SIDE[entry.side]}` +
          ` · ${agoShort(nowAt - entry.to.at)}`,
        tone: entry.side === 'bullish' ? 'up' : 'down',
        badge: { text: state.text, tone: entry.side === 'bullish' ? 'bull' : 'bear' },
        meter: entry.score,
      });
    }

    const lead = live[0]!;
    if (lead.positioning) {
      rows.push({
        label: 'Позиции',
        value: DIVERGENCE_POSITIONING[lead.positioning] ?? '—',
        tone: 'muted',
      });
    }

    rows.push({ label: 'Что происходит', value: lead.plain, full: true });

    return rows;
  },
  legend: DIVERGENCE_LEGEND,
  label: (p) => {
    const source = selectParam(p, 'source', 'all');
    if (source === 'spot') return 'Дивергенции · спот';
    if (source === 'futures') return 'Дивергенции · контракты';
    if (source === 'flow') return 'Дивергенции · потоки';
    return 'Дивергенции';
  },
};

export const INDICATOR_DEFINITIONS: readonly IndicatorDefinition[] = [
  ZONES_PRO,
  ZONES_PRO2,
  DIVERGENCE,
  ORDER_FLOW,
  ORDER_FLOW_CLUSTERS,
  ORDER_FLOW_IMBALANCE,
  ORDER_FLOW_LARGE,
  ORDER_FLOW_LIQUIDATIONS,
  ORDER_BOOK_HEATMAP,
  ORDER_FLOW_DELTA,
  CROWD_POSITIONING,
  MARKET_LIQUIDATIONS,
  MI_SCORE,
  VOLUME_PROFILE,
  LIQUIDITY_MAP,
  IMBALANCES,
  ORDER_BLOCKS,
  FIBO_CHECK,
  SESSIONS_INDICATOR,
  SMA,
  EMA,
  TRIPLE_MA,
  BOLLINGER,
  RSI,
  MACD,
  ATR,
  VOLUME,
  VOLUME_MA,
  SPOT_CVD,
  FUTURES_CVD,
];

/**
 * Слепок настроек для подписки на уведомления.
 *
 * Тот же состав, по которому рисуется линия: сервер обязан считать
 * ровно то, что человек видит на графике. Порога уведомления отдельно
 * нет намеренно — второй порог означал бы, что линия и сообщение о ней
 * считаются по-разному.
 */
export function divergenceWatchSnapshot(p: IndicatorParams): DivergenceWatchOptions {
  return {
    source: selectParam(p, 'source', 'all') as DivergenceWatchOptions['source'],
    minScore: numberParam(p, 'minScore', 40),
    sensitivity: numberParam(p, 'sensitivity', 5),
    maxGap: numberParam(p, 'maxGap', 60),
    hidden: boolParam(p, 'hidden', true),
    forming: boolParam(p, 'notifyForming', false),
    signal: {
      spot: numberParam(p, 'signalSpot', 40),
      futures: numberParam(p, 'signalFutures', 40),
      flow: numberParam(p, 'signalFlow', 40),
      hidden: boolParam(p, 'signalHidden', true),
    },
  };
}

export function defaultParams(definition: IndicatorDefinition): IndicatorParams {
  const params: Record<string, number | string | boolean> = {};
  for (const spec of definition.params) params[spec.key] = spec.default;
  return params;
}
